import { getBuildInfo } from "@/utils/build-info";

const CACHE_PREFIX = "vc:";
const MAX_ENTRIES = 30;
const DEFAULT_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CacheEntry {
  data: unknown;
  timestamp: number;
  tenantId: string;
  buildHash: string;
}

const tryParseCacheEntry = (
  rawCacheEntry: string
): CacheEntry | undefined => {
  try {
    const parsed: unknown = JSON.parse(rawCacheEntry);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as CacheEntry).timestamp !== "number" ||
      typeof (parsed as CacheEntry).tenantId !== "string" ||
      typeof (parsed as CacheEntry).buildHash !== "string"
    ) {
      return undefined;
    }
    return parsed as CacheEntry;
  } catch {
    return undefined;
  }
};

/**
 * djb2 hash of a JSON-serialized query key.
 * Returns a deterministic string suitable for use as a localStorage key.
 */
export const generateCacheKey = (queryKey: unknown): string => {
  const serialized = JSON.stringify(queryKey);
  let hash = 5381;
  for (
    let charIndex = 0;
    charIndex < serialized.length;
    charIndex++
  ) {
    hash = (hash * 33) ^ serialized.charCodeAt(charIndex);
  }
  return `${CACHE_PREFIX}${(hash >>> 0).toString(36)}`;
};

export const saveToViewCache = (
  cacheKey: string,
  data: unknown,
  tenantId: string
): void => {
  try {
    const entry: CacheEntry = {
      data,
      timestamp: Date.now(),
      tenantId,
      buildHash: getBuildInfo().buildHash
    };
    localStorage.setItem(cacheKey, JSON.stringify(entry));
    evictIfNeeded();
  } catch {
    // Quota exceeded or other storage error — cache is best-effort
  }
};

export const loadFromViewCache = (
  cacheKey: string,
  tenantId: string,
  maxAge: number = DEFAULT_MAX_AGE_MS
): unknown | undefined => {
  try {
    const rawCacheEntry = localStorage.getItem(cacheKey);
    if (!rawCacheEntry) return undefined;

    const entry = tryParseCacheEntry(rawCacheEntry);
    if (!entry) {
      localStorage.removeItem(cacheKey);
      return undefined;
    }

    if (entry.buildHash !== getBuildInfo().buildHash) {
      localStorage.removeItem(cacheKey);
      return undefined;
    }

    if (entry.tenantId !== tenantId) return undefined;
    if (Date.now() - entry.timestamp > maxAge) {
      localStorage.removeItem(cacheKey);
      return undefined;
    }

    return entry.data;
  } catch {
    return undefined;
  }
};

export const clearViewCache = (): void => {
  try {
    const keysToRemove: string[] = [];
    for (
      let storageIndex = 0;
      storageIndex < localStorage.length;
      storageIndex++
    ) {
      const key = localStorage.key(storageIndex);
      if (key?.startsWith(CACHE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      localStorage.removeItem(key);
    }
  } catch {
    // Storage access error — best-effort
  }
};

const evictIfNeeded = (): void => {
  const entries: Array<{ key: string; timestamp: number }> = [];
  const malformedKeys: string[] = [];
  for (
    let storageIndex = 0;
    storageIndex < localStorage.length;
    storageIndex++
  ) {
    const key = localStorage.key(storageIndex);
    if (!key?.startsWith(CACHE_PREFIX)) continue;

    const rawCacheEntry = localStorage.getItem(key);
    if (!rawCacheEntry) continue;

    const parsedEntry = tryParseCacheEntry(rawCacheEntry);
    if (!parsedEntry) {
      malformedKeys.push(key);
      continue;
    }
    entries.push({ key, timestamp: parsedEntry.timestamp });
  }

  for (const malformedKey of malformedKeys) {
    localStorage.removeItem(malformedKey);
  }

  if (entries.length <= MAX_ENTRIES) return;

  entries.sort(
    (olderEntry, newerEntry) =>
      olderEntry.timestamp - newerEntry.timestamp
  );
  const toEvict = entries.slice(0, entries.length - MAX_ENTRIES);
  for (const { key } of toEvict) {
    localStorage.removeItem(key);
  }
};
